import LinearProgress from '@material-ui/core/LinearProgress';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { useBooleanState } from 'react-use-object-state';
import { DeferredQueryInput } from '../../hooks/graphql/useDeferredJ1QLQuery';
import { JupiterTheme } from '../../theme/types';
import { QueryResultsChange, PaginationSortingProps } from '../../types';
import { getAlternateFormatQueryFrom } from '../../util/J1Query';
import ErrorBoundary from '../error/ErrorBoundary';
import ResultForFormat from './ResultForFormat';
import { ResultFormat } from './types';
import { J1QLResultData } from '../../../types/J1QL';
import useJ1QL from '../../hooks/useJ1QL';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    resultsContent: {
      borderTop: 'solid 1px ' + theme.palette.navy[50],
      height: '100%',
      overflow: 'auto',
      '& table': {
        marginBottom: theme.spacing(2),
      },
    },
    graphResultsContent: {
      border: 'solid 1px ' + theme.palette.navy[50],
      flex: '1 1 100%',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      '& table': {
        marginBottom: theme.spacing(2),
      },
    },
  })
);

interface Props {
  result: J1QLResultData;
  onResultsChange?: (changes: QueryResultsChange[]) => void;
  queryInput: DeferredQueryInput;
  selectedFormat: ResultFormat;
  paginationProps?: PaginationSortingProps;
  refetch?: VoidFunction;
  className?: string;
  classes?: Record<'graphView', string>;
  disableRowClick?: boolean;
  useRowDataForEntityDrawer?: boolean;
  noDataEdit?: boolean;
  parentSelectedRowID?: string | null;
  parentSetSelectedRowID?: (id: string | null) => void;
}

const ResultForFormatContainer = ({
  classes: propClasses,
  result,
  onResultsChange,
  queryInput,
  selectedFormat,
  className,
  ...props
}: Props) => {
  const classes = useStyles();

  const showAlternateFormat =
    (selectedFormat === ResultFormat.Graph && result.type !== 'tree') ||
    (selectedFormat === ResultFormat.Table && result.type === 'tree');

  return (
    <div
      className={clsx(
        selectedFormat === ResultFormat.Graph
          ? [classes.graphResultsContent, propClasses?.graphView]
          : classes.resultsContent,
        className
      )}
    >
      {showAlternateFormat ? (
        <AlternateFormat
          queryInput={queryInput}
          selectedFormat={selectedFormat}
          {...props}
        />
      ) : (
        <ResultForFormat
          query={queryInput.query}
          format={selectedFormat}
          result={result}
          onResultsChange={onResultsChange}
          {...props}
        />
      )}
    </div>
  );
};

interface ChildProps {
  queryInput: DeferredQueryInput;
  selectedFormat: ResultFormat;
  paginationProps?: PaginationSortingProps;
  refetch?: VoidFunction;
  disableRowClick?: boolean;
  useRowDataForEntityDrawer?: boolean;
  noDataEdit?: boolean;
}

const AlternateFormat = ({
  selectedFormat,
  queryInput: { query, ...queryInput },
  paginationProps,
  ...props
}: ChildProps) => {
  const refetching = useBooleanState(false);
  const formattedQuery = getAlternateFormatQueryFrom(query);
  const {
    result,
    fetchMore,
    canFetchMore,
    totalResultCount,
    loading,
    refetch,
  } = useJ1QL(formattedQuery, {
    ...queryInput,
  });

  const handleRefetch = async () => {
    refetching.setTrue();
    await refetch();
    refetching.setFalse();
  };

  // If we have result data and are loading, that means we are fetching more, so don't
  // nuke the screen by only showing LinearProgress
  if ((loading && !result) || refetching.state) {
    return <LinearProgress />;
  }

  return (
    <ErrorBoundary>
      <ResultForFormat
        {...props}
        query={query}
        format={selectedFormat}
        result={result as J1QLResultData}
        refetch={handleRefetch}
        paginationProps={
          // It is important to conditionally check for pagination props here before redefining them via
          // the results of our new query.  In parents of this component, the presence of a paginationProps
          // prop value indicates that we want to allow pagination for a QueryResult.  So here, we are essentially
          // checking if pagination is enabled, and if so, then populate the pagination props with the results
          // of our new query.
          // THIS IS TECHNICAL DEBT (caused by too much prop drilling) that should be fixed when we refactor Landing
          paginationProps
            ? {
                loadMore: fetchMore,
                loading,
                hasNextPage: canFetchMore,
                totalResultCount,
              }
            : undefined
        }
      />
    </ErrorBoundary>
  );
};

export default ResultForFormatContainer;
